import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { type AuthProviders, getAuthProviders, oauthStartUrl } from '../lib/auth.js';
import type { AuthMethod } from '../lib/lastAuth.js';
import { captureReferralCode } from '../lib/referral.js';

const btnClass =
  'relative flex w-full items-center justify-center gap-2.5 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800';

/** Small "Last used" badge shown on the method the user authenticated with last. */
export function LastUsedPill() {
  return (
    <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
      Last used
    </span>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <path fill="#f25022" d="M1 1h9v9H1z" />
      <path fill="#7fba00" d="M11 1h9v9h-9z" />
      <path fill="#00a4ef" d="M1 11h9v9H1z" />
      <path fill="#ffb900" d="M11 11h9v9h-9z" />
    </svg>
  );
}

/**
 * "Continue with Google / Microsoft" buttons. Renders only the providers the API
 * reports as configured (fail-soft: nothing shows until keys are set). Buttons are
 * plain full-page navigations to the API's /start route. `verb` lets callers say
 * "Sign in" vs "Sign up".
 */
export function OAuthButtons({
  verb = 'Continue',
  lastUsed = null,
}: { verb?: string; lastUsed?: AuthMethod | null }) {
  const { baseUrl } = useAuth();
  const [providers, setProviders] = useState<AuthProviders>({ google: false, microsoft: false });
  // Preserve referral attribution through the OAuth round-trip (the ?ref on the
  // register page would otherwise be lost when the browser leaves for the provider).
  const ref = captureReferralCode();

  useEffect(() => {
    let alive = true;
    getAuthProviders(baseUrl).then((p) => {
      if (alive) setProviders(p);
    });
    return () => {
      alive = false;
    };
  }, [baseUrl]);

  if (!providers.google && !providers.microsoft) return null;

  return (
    <div className="mb-5">
      <div className="space-y-2.5">
        {providers.google && (
          <a className={btnClass} href={oauthStartUrl(baseUrl, 'google', ref)}>
            <GoogleIcon /> {verb} with Google
            {lastUsed === 'google' && <LastUsedPill />}
          </a>
        )}
        {providers.microsoft && (
          <a className={btnClass} href={oauthStartUrl(baseUrl, 'microsoft', ref)}>
            <MicrosoftIcon /> {verb} with Microsoft
            {lastUsed === 'microsoft' && <LastUsedPill />}
          </a>
        )}
      </div>
      <div className="my-5 flex items-center gap-3 text-xs text-neutral-400">
        <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
        or
        <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
      </div>
    </div>
  );
}
